import { Avatar, Chip, type ChipSize } from '@/components/ui/design-system';
import { useResolvedMediaUrl } from '@/hooks/use-resolved-media-url';
import { isPhoneLike } from '@/lib/phone-number';

/** Pre-composed Chip + Avatar for **external contacts**. Always
 *  `color="primary"`. Picks the Avatar variant (`image` / `initial` /
 *  `unknown`) from what's available, resolves API-signed image URLs
 *  internally, and skips initials when the "name" is actually a phone
 *  number.
 *
 *  Use this for contacts in tables, panels, and rows. For team members use
 *  `MemberAvatar` (`@/components/ui/member-avatar`) which forces `highlight`. */
export const ContactAvatar = ({
  name,
  imageUrl,
  size = 'md',
  className,
}: {
  name?: string | null;
  imageUrl?: string | null;
  size?: ChipSize;
  className?: string;
}) => {
  const resolved = useResolvedMediaUrl(imageUrl ?? undefined);
  const displayName = name && !isPhoneLike(name) ? name : undefined;

  return (
    <Chip size={size} color="primary" className={className}>
      {resolved ? (
        <Avatar type="image" src={resolved} name={displayName || 'Contact'} />
      ) : displayName ? (
        <Avatar type="initial" name={displayName} />
      ) : (
        <Avatar type="unknown" />
      )}
    </Chip>
  );
};
